import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, EmptyState, StatusBadge } from "../components/Shared";
import { Search, ShieldAlert, KeyRound, Send, Trash2, Loader2, CalendarClock } from "lucide-react";
import { GoLivePanel } from "../components/GoLivePanel";
import { LegalSettings } from "../components/LegalSettings";

const ROLES = ["ALL", "MEMBER", "COACH", "CO_ADMIN", "MASTER_ADMIN"];
const STATUSES = ["ALL", "PENDING", "ACTIVE", "SUSPENDED", "DEACTIVATED"];
const ASSIGNABLE = ["MEMBER", "COACH", "CO_ADMIN"];

export default function AdminUsers() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [permFor, setPermFor] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState([]);
  const [link, setLink] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [expFor, setExpFor] = useState(null);
  const [expDate, setExpDate] = useState("");
  const [expReason, setExpReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&role=${role}&status=${status}`);
      setItems(data.data.items);
    } catch (e) { toast.error(extractError(e).message); }
  }, [q, role, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/permissions").then(({ data }) => setCatalog(data.data)).catch(() => {}); }, []);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(extractError(e).message); }
  };

  const setStatusFor = (u, next) => act(() => api.patch(`/admin/users/${u.user_id}/status`, { status: next }), t("users.statusChanged"));
  const setRoleFor = (u, next) => act(() => api.patch(`/admin/users/${u.user_id}/role`, { role: next }), t("users.roleChanged"));

  const savePerms = async () => {
    await act(() => api.put(`/admin/users/${permFor.user_id}/permissions`, { permissions: selected }), t("common.saved"));
    setPermFor(null);
  };

  const sendLink = async (u, kind) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/${kind}`);
      setLink(data.data.preview_link || "");
      toast.success(kind === "reset-link" ? t("users.resetSent") : t("users.inviteResent"));
    } catch (e) { toast.error(extractError(e).message); }
  };

  const removeUser = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteFor.user_id}`, { data: { confirm_email: confirmEmail } });
      const wiped = Object.values(data.data.deleted).reduce((a, b) => a + b, 0);
      toast.success(t("users.deleted").replace("{count}", wiped));
      setDeleteFor(null);
      setConfirmEmail("");
      load();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setDeleting(false); }
  };

  const saveExpiration = async (u) => {
    if (!u.enrollment?.enrollment_id) return;
    await act(() => api.patch(`/admin/enrollments/${u.enrollment.enrollment_id}/expiration`, { expiration_date: expDate, reason: expReason }), t("users.expirationSaved"));
    setExpFor(null); setExpDate(""); setExpReason("");
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <div data-testid="admin-users-page" className="space-y-8">
      <PageHeader eyebrow={t("users.eyebrow")} title={t("users.title")} subtitle={t("users.subtitle")} />

      <GoLivePanel />
      <LegalSettings />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-sm">{t("users.search")}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="users-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="nama / email" className="pl-9 bg-[#1C202E] border-white/10 text-white h-10" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-sm">{t("dashboard.role")}</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="users-role-filter" className="bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-sm">{t("common.status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="users-status-filter" className="bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {link && (
        <p className="text-xs text-slate-400 break-all">
          {t("auth.previewLabel")}: <a data-testid="users-preview-link" href={link} className="text-[#10B981] underline">{link}</a>
        </p>
      )}

      {items.length === 0 ? <EmptyState testId="admin-users-empty" text={t("users.empty")} /> : (
        <div className="space-y-3">
          {items.map((u) => (
            <Card key={u.user_id} data-testid={`user-row-${u.user_id}`} className="bg-[#161923] border-white/5">
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{u.full_name || "—"}</span>
                      <span className="text-xs text-slate-500">{u.email}</span>
                      <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">{u.role}</span>
                      <StatusBadge status={u.account_status} testId={`user-status-${u.user_id}`} />
                      {!u.email_verified && <span className="text-[10px] uppercase tracking-wider text-amber-300">{t("auth.verifyRequired")}</span>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                      <span>{t("coach.activeEnrollments")}: {u.active_enrollments}</span>
                      {u.whatsapp_number && <span>WA: {u.whatsapp_number}</span>}
                      {u.last_login && <span>{t("users.lastLogin")}: {new Date(u.last_login).toLocaleDateString()}</span>}
                      {u.extra_permissions?.length > 0 && <span className="text-slate-300">{u.extra_permissions.length} {t("users.extraPerms")}</span>}
                    </div>
                  </div>

                  {u.role !== "MASTER_ADMIN" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={u.role} onValueChange={(v) => setRoleFor(u, v)}>
                        <SelectTrigger data-testid={`user-role-select-${u.user_id}`} className="w-[130px] bg-[#1C202E] border-white/10 text-white h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                          {ASSIGNABLE.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {u.account_status !== "ACTIVE" && (
                        <Button data-testid={`user-activate-${u.user_id}`} size="sm" onClick={() => setStatusFor(u, "ACTIVE")} className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white text-xs">{t("users.activate")}</Button>
                      )}
                      {u.account_status !== "SUSPENDED" && (
                        <Button data-testid={`user-suspend-${u.user_id}`} size="sm" onClick={() => setStatusFor(u, "SUSPENDED")} className="h-9 bg-amber-600 hover:bg-amber-500 text-white text-xs"><ShieldAlert className="h-3.5 w-3.5 mr-1" />{t("users.suspend")}</Button>
                      )}
                      {u.account_status !== "DEACTIVATED" && (
                        <Button data-testid={`user-deactivate-${u.user_id}`} size="sm" onClick={() => setStatusFor(u, "DEACTIVATED")} className="h-9 bg-red-600 hover:bg-red-500 text-white text-xs">{t("users.deactivate")}</Button>
                      )}
                      <Button data-testid={`user-reset-${u.user_id}`} size="sm" variant="ghost" onClick={() => sendLink(u, "reset-link")} className="h-9 text-slate-300 hover:bg-white/5 text-xs"><KeyRound className="h-3.5 w-3.5 mr-1" />{t("users.sendReset")}</Button>
                      {!u.email_verified && (
                        <Button data-testid={`user-reinvite-${u.user_id}`} size="sm" variant="ghost" onClick={() => sendLink(u, "resend-invitation")} className="h-9 text-slate-300 hover:bg-white/5 text-xs"><Send className="h-3.5 w-3.5 mr-1" />{t("users.resendInvite")}</Button>
                      )}
                      {u.role === "CO_ADMIN" && (
                        <Button data-testid={`user-perms-${u.user_id}`} size="sm" variant="ghost" onClick={() => { setPermFor(u); setSelected(u.extra_permissions || []); }} className="h-9 text-slate-300 hover:bg-white/5 text-xs">{t("users.permissions")}</Button>
                      )}
                      <Button data-testid={`user-delete-${u.user_id}`} size="sm" variant="ghost"
                        onClick={() => { setDeleteFor(deleteFor?.user_id === u.user_id ? null : u); setConfirmEmail(""); }}
                        className="h-9 text-red-400 hover:bg-red-500/10 text-xs">
                        <Trash2 className="h-3.5 w-3.5 mr-1" />{t("users.delete")}
                      </Button>
                    </div>
                  )}
                </div>

                {u.enrollment && (
                  <div data-testid={`user-enrollment-${u.user_id}`} className="pt-3 border-t border-white/5">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
                      <span className="uppercase tracking-widest text-slate-500 font-medium">Enrollment</span>
                      <StatusBadge status={u.enrollment.status} testId={`user-enrollment-status-${u.user_id}`} />
                      <span className="text-slate-400">{t("users.startDate")}: <span className="text-slate-200">{fmtDate(u.enrollment.enrollment_start_date)}</span></span>
                      <span className="text-slate-400">{t("users.expirationDate")}: <span data-testid={`user-enrollment-exp-${u.user_id}`} className="text-slate-200">{fmtDate(u.enrollment.enrollment_expiration_date)}</span></span>
                      <span className={`font-semibold ${u.enrollment.status === "ACTIVE" ? "text-emerald-300" : "text-red-300"}`} data-testid={`user-enrollment-days-${u.user_id}`}>{u.enrollment.days_remaining} {t("users.daysRemaining")}</span>
                      {u.enrollment.batch_code && <span className="text-slate-500">· {u.enrollment.batch_code}</span>}
                      {u.role !== "MASTER_ADMIN" && (
                        <Button data-testid={`user-edit-exp-${u.user_id}`} size="sm" variant="ghost"
                          onClick={() => { const cur = u.enrollment.enrollment_expiration_date; setExpFor(expFor?.user_id === u.user_id ? null : u); setExpDate(cur ? new Date(cur).toISOString().slice(0, 10) : ""); setExpReason(""); }}
                          className="h-8 text-slate-300 hover:bg-white/5 text-xs"><CalendarClock className="h-3.5 w-3.5 mr-1" />{t("users.editExpiration")}</Button>
                      )}
                    </div>
                    {expFor?.user_id === u.user_id && (
                      <div data-testid={`user-exp-form-${u.user_id}`} className="mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
                        <div className="space-y-1">
                          <Label className="text-slate-300 text-xs">{t("users.newExpiration")}</Label>
                          <Input type="date" data-testid={`user-exp-date-${u.user_id}`} value={expDate} onChange={(e) => setExpDate(e.target.value)} className="bg-[#1C202E] border-white/10 text-white h-9 w-[170px]" />
                        </div>
                        <div className="space-y-1 flex-1">
                          <Label className="text-slate-300 text-xs">{t("users.reason")}</Label>
                          <Input data-testid={`user-exp-reason-${u.user_id}`} value={expReason} onChange={(e) => setExpReason(e.target.value)} placeholder={t("users.reasonPlaceholder")} className="bg-[#1C202E] border-white/10 text-white h-9" />
                        </div>
                        <Button data-testid={`user-exp-save-${u.user_id}`} disabled={!expDate || expReason.trim().length < 3} onClick={() => saveExpiration(u)} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-9 font-semibold">{t("common.save")}</Button>
                      </div>
                    )}
                  </div>
                )}

                {permFor?.user_id === u.user_id && (
                  <div data-testid={`user-perms-form-${u.user_id}`} className="pt-4 border-t border-white/5 space-y-3">
                    <p className="text-xs text-slate-400">{t("users.permissionsHint")}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {catalog.map((p) => (
                        <label key={p.permission_id} className="flex items-center gap-2 text-xs text-slate-300">
                          <input
                            type="checkbox"
                            data-testid={`perm-${p.name}`}
                            checked={selected.includes(p.name)}
                            onChange={(e) => setSelected(e.target.checked ? [...selected, p.name] : selected.filter((n) => n !== p.name))}
                            className="accent-[#10B981]"
                          />
                          <span className="font-mono">{p.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setPermFor(null)} className="text-slate-300 h-9">{t("common.cancel")}</Button>
                      <Button data-testid={`user-perms-save-${u.user_id}`} onClick={savePerms} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-9 font-semibold">{t("common.save")}</Button>
                    </div>
                  </div>
                )}
                {deleteFor?.user_id === u.user_id && (
                  <div data-testid={`user-delete-confirm-${u.user_id}`} className="pt-4 border-t border-red-500/20 space-y-3">
                    <p className="text-sm text-red-300 font-semibold">{t("users.deleteTitle")}</p>
                    <p className="text-xs text-slate-400">{t("users.deleteWarning")}</p>
                    <Label className="text-slate-300 text-sm">{t("users.deleteConfirmLabel").replace("{email}", u.email)}</Label>
                    <Input data-testid={`user-delete-email-${u.user_id}`} value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                      placeholder={u.email} className="bg-[#1C202E] border-white/10 text-white h-10" />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setDeleteFor(null)} className="text-slate-300 h-9">{t("common.cancel")}</Button>
                      <Button data-testid={`user-delete-submit-${u.user_id}`} disabled={deleting || confirmEmail.trim().toLowerCase() !== u.email.toLowerCase()}
                        onClick={removeUser} className="bg-red-600 hover:bg-red-500 text-white h-9 font-semibold">
                        {deleting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("common.loading")}</> : t("users.deleteConfirmBtn")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
